import { Injectable } from "@angular/core";
import { DateAdapter } from "@angular/material/core";
import {
  DateRange,
  MatCalendar,
  MatDatepickerInputEvent,
  yearsPerPage,
} from "@angular/material/datepicker";

import moment, { Moment } from "moment";

import { LocalizationUtils } from "@shared/utils";

@Injectable({
  providedIn: "root",
})
export class DatepickerService<D> {
  constructor(private dateAdapter: DateAdapter<D>) {}

  readonly datePickerDateFormat = LocalizationUtils.getLocaleDatepickerFormat();

  private readonly formatsWithMonthText = [
    "MMM DD, YYYY",
    "MMM DD, YY",
    "MMMM DD, YYYY",
    "MMMM DD, YY",
    "MMM DD YYYY",
    "MMM DD YY",
    "MMMM DD YYYY",
    "MMMM DD YY",
  ];

  formatMinAndMaxYearLabels(
    calendar: MatCalendar<D>,
  ): [minYearLabel: string, maxYearLabel: string] {
    const activeYear = this.dateAdapter.getYear(calendar.activeDate);

    const offset = this.getActiveOffset(
      this.dateAdapter,
      calendar.activeDate,
      calendar.minDate,
      calendar.maxDate,
    );

    const minYearOfPage = activeYear - offset;
    const maxYearOfPage = minYearOfPage + yearsPerPage - 1;
    const minYearLabel = this.dateAdapter.getYearName(
      this.dateAdapter.createDate(minYearOfPage, 0, 1),
    );
    const maxYearLabel = this.dateAdapter.getYearName(
      this.dateAdapter.createDate(maxYearOfPage, 0, 1),
    );

    return [minYearLabel, maxYearLabel];
  }

  normalizeValue(event: MatDatepickerInputEvent<string, DateRange<string>> | Event): string {
    let value: string | Moment = (event.target as HTMLInputElement).value;

    if (moment.isMoment(value)) {
      value = value.format();
    } else if (typeof value === "string") {
      const momentValue = this.stringToMoment(value);

      value = momentValue.isValid() ? momentValue.format() : value;
    }

    return value;
  }

  private detectAndParseDateWithMonthText(input: string) {
    if (!input) {
      return null;
    }

    const inputWithoutExtraSpaces = input.trim().replace(/\s+/g, " ");

    for (const format of this.formatsWithMonthText) {
      const momentObject = moment(inputWithoutExtraSpaces, format, true);

      if (momentObject.isValid()) {
        return momentObject.format(this.datePickerDateFormat);
      }
    }

    return null;
  }

  getAutoCorrectedValue(value: string): string {
    const parsedDate = this.detectAndParseDateWithMonthText(value);

    if (parsedDate) {
      value = parsedDate;
    }

    const dateFormatRegexes = [
      /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
      /^\d{2}\/\d{2}\/\d{2}$/, // DD/MM/YY
      /^\d{2}\.\d{2}\.\d{4}$/, // DD.MM.YYYY
      /^\d{2}\.\d{2}\.\d{2}$/, // DD.MM.YY
    ];

    const matchesFormat = dateFormatRegexes.some((regex) => regex.test(value));

    if (!matchesFormat) {
      return null;
    }

    let correctedValue = value;

    const [firstNumber, secondNumber, shortYear] = value.split(".");

    if (/^\d{2}\.\d{2}\.\d{2}$/.test(value)) {
      const fullYear = "20" + shortYear;

      correctedValue = `${firstNumber}/${secondNumber}/${fullYear}`;
    } else if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
      const [firstNumber, secondNumber, year] = value.split(".");

      correctedValue = `${firstNumber}/${secondNumber}/${year}`;
    } else if (/^\d{2}\/\d{2}\/\d{2}$/.test(value)) {
      const [firstNumber, secondNumber, shortYear] = value.split("/");
      const fullYear = "20" + shortYear;

      correctedValue = `${firstNumber}/${secondNumber}/${fullYear}`;
    }

    const momentValue = this.stringToMoment(correctedValue);

    if (momentValue.isValid()) {
      return correctedValue;
    }

    return null;
  }

  stringToMoment(value: string): Moment {
    return moment.utc(value, this.datePickerDateFormat, true);
  }

  private getActiveOffset<D>(
    dateAdapter: DateAdapter<D>,
    activeDate: D,
    minDate: D | null,
    maxDate: D | null,
  ): number {
    const activeYear = dateAdapter.getYear(activeDate);
    const startingYear = this.getStartingYear(dateAdapter, minDate, maxDate);

    return this.euclideanModulo(activeYear - startingYear, yearsPerPage);
  }

  private getStartingYear<D>(
    dateAdapter: DateAdapter<D>,
    minDate: D | null,
    maxDate: D | null,
  ): number {
    let startingYear = 0;

    if (maxDate) {
      const maxYear = dateAdapter.getYear(maxDate);

      startingYear = maxYear - yearsPerPage + 1;
    } else if (minDate) {
      startingYear = dateAdapter.getYear(minDate);
    }

    return startingYear;
  }

  private euclideanModulo(a: number, b: number): number {
    return ((a % b) + b) % b;
  }
}
